import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ActionIcon, Button, Input, Modal, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../../../components/buttons/DeletePopover';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../../../components/table/CustomTable';
import useFetch from '../../../../../hooks/useFetch';
import useFilters from '../../../../../hooks/useFilters';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import { menuRoutes } from '../../../../../router/menu';
import { GroupCodeService } from '../../../../../services/groupCode/groupCodeService';
import OrderProfileLayout from '../../OrderProfileLayout';
import { MdOutlineModeEditOutline, MdDeleteOutline } from 'react-icons/md';

interface GroupCodeRow {
    id: string;
    code: string;
    startDate?: string | null;
    endDate?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    orderId?: string;
    orderCode?: string;
    corporationId?: string | null;
    corporationName?: string | null;
    contractsCount: number;
}

const columnOrder = [
    'id',
    'code',
    'corporationName',
    'contractsCount',
    'startDate',
    'endDate',
    'createdAt',
    'updatedAt',
];

const formatDate = (value: any): string => {
    if (!value) return 'N/A';
    const raw = typeof value === 'object' ? value?.date : value;
    if (!raw) return 'N/A';
    return typeof raw === 'string' ? raw.slice(0, 10) : 'N/A';
};

const toDateInputValue = (value: any): string => {
    if (!value) return '';
    const raw = typeof value === 'object' ? value?.date : value;
    if (!raw || typeof raw !== 'string') return '';
    return raw.slice(0, 10);
};

const OrderGroupCodes = () => {
    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { filters } = useFilters();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [rowToEdit, setRowToEdit] = useState<GroupCodeRow | null>(null);
    const [editingCode, setEditingCode] = useState('');
    const [editingStartDate, setEditingStartDate] = useState('');
    const [editingEndDate, setEditingEndDate] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const [data, loading, , refetch] = useFetch(useCallback(async () => {
        if (!id) return { success: true, data: [] };
        const response = await (new GroupCodeService()).listByOrder({ orderId: id });
        return response.getResponseData();
    }, [id]));

    const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const orderedData = useMemo(() => {
        return rows.map((row: any) => {
            const orderedRow: any = {};
            columnOrder.forEach((key) => {
                orderedRow[key] = row[key];
            });
            return orderedRow;
        });
    }, [rows]);

    const openEditModal = (row: GroupCodeRow) => {
        setRowToEdit(row);
        setEditingCode(row.code || '');
        setEditingStartDate(toDateInputValue(row.startDate));
        setEditingEndDate(toDateInputValue(row.endDate));
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setRowToEdit(null);
        setEditingCode('');
        setEditingStartDate('');
        setEditingEndDate('');
    };

    const handleSaveCode = async () => {
        if (!rowToEdit?.id) return;
        const codeToSave = editingCode.trim();
        if (!codeToSave) {
            toast.error('El código es obligatorio');
            return;
        }

        if (!editingStartDate || !editingEndDate) {
            toast.error('Las fechas de inicio y fin son obligatorias');
            return;
        }

        if (editingStartDate > editingEndDate) {
            toast.error('La fecha de inicio no puede ser posterior a la fecha de fin');
            return;
        }

        setIsSaving(true);
        try {
            const response = await (await (new GroupCodeService()).editCode({
                groupCodeId: rowToEdit.id,
                code: codeToSave,
                startDate: editingStartDate,
                endDate: editingEndDate,
            })).getResponseData();

            if (response.success) {
                toast.success('Código de grupo actualizado con éxito');
                closeEditModal();
                refetch();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            toast.error('Error al actualizar el código de grupo');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (groupCodeId: string) => {
        const response = await (await (new GroupCodeService()).delete({ groupCodeId })).getResponseData();
        if (response.success) {
            toast.success('Código de grupo eliminado con éxito');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    return (
        <OrderProfileLayout>
            {loading
                ? <Loader height='60vh' />
                : (
                    <>
                        <CustomTable
                            id='order-group-codes-table'
                            data={orderedData}
                            columnOrder={columnOrder}
                            filters={filters}
                            search={false}
                            filter={false}
                            pagination={false}
                            overrideColumns={[
                                {
                                    key: 'code',
                                    columnNameKey: 'Código de Grupo',
                                    render: (row: any) => (
                                        <span className='font-semibold text-primary'>{row.row.original.code || 'N/A'}</span>
                                    ),
                                },
                                {
                                    key: 'corporationName',
                                    columnNameKey: 'Organizadora',
                                    render: (row: any) => row.row.original.corporationName || 'N/A',
                                },
                                {
                                    key: 'contractsCount',
                                    columnNameKey: 'Contratos Asignados',
                                    render: (row: any) => (
                                        <Link
                                            className='font-semibold text-primary'
                                            to={`${menuRoutes.orders.path}/${id}/profile${menuRoutes.orders.profile.contracts}`}
                                        >
                                            {row.row.original.contractsCount ?? 0}
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'startDate',
                                    columnNameKey: 'Fecha Inicio',
                                    render: (row: any) => formatDate(row.row.original.startDate),
                                },
                                {
                                    key: 'endDate',
                                    columnNameKey: 'Fecha Fin',
                                    render: (row: any) => formatDate(row.row.original.endDate),
                                },
                                {
                                    key: 'createdAt',
                                    columnNameKey: 'Creado',
                                    render: (row: any) => formatDate(row.row.original.createdAt),
                                },
                                {
                                    key: 'updatedAt',
                                    columnNameKey: 'Actualizado',
                                    render: (row: any) => formatDate(row.row.original.updatedAt),
                                },
                            ]}
                            actions={[
                                {
                                    label: 'Edit',
                                    permissions: { group: 'group_codes', permission: 'edit_group_codes' },
                                    render: (row: any) => (
                                        <Tooltip size='sm' content='Editar código' placement='top' color='invert'>
                                            <ActionIcon
                                                as='span'
                                                size='sm'
                                                variant='outline'
                                                className='hover:!border-gray-900 hover:text-gray-700 cursor-pointer'
                                                onClick={() => openEditModal(row)}
                                            >
                                                <MdOutlineModeEditOutline className='h-4 w-4' size={20} />
                                            </ActionIcon>
                                        </Tooltip>
                                    ),
                                },
                                {
                                    label: 'Delete',
                                    permissions: { group: 'group_codes', permission: 'delete_group_codes' },
                                    render: (row: any) => {
                                        const hasContracts = Number(row.contractsCount ?? 0) > 0;

                                        if (hasContracts) {
                                            return (
                                                <Tooltip size='sm' content='No se puede eliminar porque tiene contratos asignados' placement='top' color='invert'>
                                                    <ActionIcon as='span' size='sm' variant='outline' className='opacity-50 cursor-not-allowed'>
                                                        <MdDeleteOutline className='h-4 w-4' size={20} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            );
                                        }

                                        return (
                                            <Tooltip size='sm' content='Eliminar' placement='top' color='invert'>
                                                <div>
                                                    <DeletePopover
                                                        title='Eliminar código de grupo'
                                                        description={`¿Estás seguro de que deseas eliminar el código ${row.code}?`}
                                                        onDelete={() => handleDelete(row.id)}
                                                    />
                                                </div>
                                            </Tooltip>
                                        );
                                    }
                                }
                            ]}
                        />

                        <Modal
                            isOpen={isEditModalOpen}
                            onClose={closeEditModal}
                            overlayClassName='backdrop-blur'
                            containerClassName='!shadow-xl'
                            className='z-[999] [&_.pointer-events-none]:overflow-visible'
                        >
                            <div className='m-auto px-7 pt-6 pb-6'>
                                <Text className='text-xl mb-5'>Editar Código de Grupo</Text>

                                <div className='space-y-3 mb-5'>
                                    <Input
                                        label='Código de grupo'
                                        value={editingCode}
                                        onChange={(event) => setEditingCode(event.target.value)}
                                    />
                                    <Input
                                        type='date'
                                        label='Fecha inicio'
                                        value={editingStartDate}
                                        onChange={(event) => setEditingStartDate(event.target.value)}
                                        max={editingEndDate || undefined}
                                    />
                                    <Input
                                        type='date'
                                        label='Fecha fin'
                                        value={editingEndDate}
                                        onChange={(event) => setEditingEndDate(event.target.value)}
                                        min={editingStartDate || undefined}
                                    />
                                    <Input label='Organizadora' value={rowToEdit?.corporationName || 'N/A'} readOnly />
                                    <Input label='Contratos asignados' value={String(rowToEdit?.contractsCount ?? 0)} readOnly />
                                </div>

                                <div className='flex justify-end gap-2'>
                                    <Button variant='outline' color='secondary' onClick={closeEditModal}>
                                        Cancelar
                                    </Button>
                                    <Button
                                        onClick={handleSaveCode}
                                        isLoading={isSaving}
                                        disabled={isSaving || !userCan('edit_group_codes', 'group_codes')}
                                    >
                                        Guardar
                                    </Button>
                                </div>
                            </div>
                        </Modal>
                    </>
                )
            }
        </OrderProfileLayout>
    );
};

export default OrderGroupCodes;
